<script setup lang="ts">
import { computed, ref } from 'vue'
import { vOpenPicker } from '@/lib/openPicker'
import type { BusinessHours } from './formDefinitions'
import { groupsFromRows, rowsFromGroups, type HoursGroup } from './hoursGroups'

// One weekly schedule as groups of days that share hours, so a run of days
// with the same hours is set once. Days in no group are closed. The stored
// value stays one row per day; it's rebuilt from the groups on every edit.
const props = defineProps<{
  /** Id for the first time field, so a validation error can focus it. */
  firstId: string
  /** Names the schedule for screen readers, e.g. "Business" or "Ad". */
  name: string
}>()
const rows = defineModel<BusinessHours[]>({ required: true })

interface EditorGroup extends HoursGroup {
  key: number
}
let nextKey = 0
function editorGroup(group: HoursGroup): EditorGroup {
  return { ...group, days: [...group.days], key: nextKey++ }
}

const days = computed(() => rows.value.map((row) => row.day))
const restored = groupsFromRows(rows.value)
const groups = ref<EditorGroup[]>(
  (restored.length ? restored : [{ days: [], opens: '', closes: '' }]).map(editorGroup),
)
const closedDays = computed(() =>
  days.value.filter((day) => !groups.value.some((group) => group.days.includes(day))),
)
const anyDayPicked = computed(() => closedDays.value.length < days.value.length)

function store() {
  rows.value = rowsFromGroups(days.value, groups.value)
}
// A day belongs to one group at a time, so picking it here takes it from any other.
function toggleDay(group: EditorGroup, day: string) {
  if (group.days.includes(day)) {
    group.days = group.days.filter((picked) => picked !== day)
  } else {
    for (const other of groups.value) other.days = other.days.filter((picked) => picked !== day)
    group.days = days.value.filter(
      (candidate) => candidate === day || group.days.includes(candidate),
    )
  }
  store()
}
function setTime(group: EditorGroup, edge: 'opens' | 'closes', event: Event) {
  group[edge] = (event.target as HTMLInputElement).value
  store()
}
function addGroup() {
  groups.value.push(editorGroup({ days: [], opens: '', closes: '' }))
}
function removeGroup(index: number) {
  groups.value.splice(index, 1)
  store()
}
function listDays(list: string[]) {
  return list.length === days.value.length ? 'every day' : list.join(', ')
}
</script>

<template>
  <div class="hours-editor">
    <div
      v-for="(group, index) in groups"
      :key="group.key"
      class="hours-group"
      role="group"
      :aria-label="`${props.name} hours ${index + 1}`"
    >
      <div class="day-chips">
        <button
          v-for="day in days"
          :key="day"
          type="button"
          class="day-chip"
          :aria-pressed="group.days.includes(day)"
          :aria-label="day"
          @click="toggleDay(group, day)"
        >
          {{ day.slice(0, 3) }}
        </button>
      </div>
      <div class="times">
        <label
          >Opens<input
            :id="index === 0 ? firstId : undefined"
            v-open-picker
            type="time"
            :value="group.opens"
            :aria-label="`${props.name} opening time`"
            @input="setTime(group, 'opens', $event)"
        /></label>
        <label
          >Closes<input
            v-open-picker
            type="time"
            :value="group.closes"
            :aria-label="`${props.name} closing time`"
            @input="setTime(group, 'closes', $event)"
        /></label>
        <button
          v-if="groups.length > 1"
          type="button"
          class="text-button remove"
          @click="removeGroup(index)"
        >
          Remove
        </button>
      </div>
    </div>
    <button
      v-if="closedDays.length && anyDayPicked"
      type="button"
      class="text-button"
      @click="addGroup"
    >
      + Add different hours for other days
    </button>
    <p class="summary" aria-live="polite">
      {{
        !anyDayPicked
          ? 'Pick the days these hours apply to.'
          : closedDays.length
            ? `Closed: ${listDays(closedDays)}.`
            : 'Open every day.'
      }}
    </p>
  </div>
</template>

<style scoped>
.hours-editor {
  display: grid;
  gap: 0.75rem;
}
.hours-group {
  display: grid;
  gap: 0.6rem;
  padding: 0.8rem 0;
  border-bottom: 1px solid #e2e6df;
}
.day-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}
.day-chip {
  min-width: 3rem;
  padding: 0.45rem 0.6rem;
  border: 1px solid #aeb9ac;
  border-radius: 999px;
  background: #fff;
  color: var(--ink);
  font: inherit;
  font-size: 0.88rem;
  font-weight: 650;
  cursor: pointer;
}
.day-chip[aria-pressed='true'] {
  background: var(--pine);
  border-color: var(--pine);
  color: #fff;
}
.times {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
  gap: 0.6rem;
  align-items: end;
}
label {
  display: grid;
  gap: 0.35rem;
  font-size: 0.93rem;
  font-weight: 650;
}
input {
  box-sizing: border-box;
  width: 100%;
  padding: 0.62rem 0.7rem;
  border: 1px solid #aeb9ac;
  border-radius: 3px;
  background: #fff;
  color: var(--ink);
  font: inherit;
  font-weight: 400;
  cursor: pointer;
}
input:focus-visible,
button:focus-visible {
  outline: 3px solid #b9c967;
  outline-offset: 2px;
}
.text-button {
  justify-self: start;
  padding: 0;
  border: 0;
  background: none;
  color: var(--pine);
  font: inherit;
  font-weight: 650;
  text-decoration: underline;
  cursor: pointer;
}
.remove {
  padding-bottom: 0.65rem;
}
.summary {
  margin: 0;
  font-size: 0.88rem;
  color: #526057;
}
@media (max-width: 600px) {
  .day-chips {
    flex-wrap: nowrap;
    gap: 0.25rem;
  }
  .day-chip {
    flex: 1 1 0;
    min-width: 0;
    padding: 0.45rem 0;
  }
  .times {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }
  .remove {
    grid-column: 1 / -1;
    justify-self: end;
    padding-bottom: 0;
  }
}
</style>
